import type { Ref } from "vue"
import type { Toast } from "../types"
import { canIntersect, checkIntersections } from "./intersect"
import { popToast, type UseToastsState } from "."
import { getToastDuration, getToastElapsed, toastAddImpulse, toastAddPseudoImpulse } from "./utils"
import { TOAST_SPACE_BOTTOM } from "../constants"
import { VEC2, clamp } from "math";


const getGravity = (toast: Toast, dt: number) => {
  const G = 9.28;
  return VEC2(0, -1).scale(dt * G * toast.mass);
}


const applyGravity = (toast: Ref<Toast>, _dt: number) => {
  toast.value.velocity = toast.value.velocity.add(getGravity(toast.value,1));
}

const tryToRest = (toast: Ref<Toast>, dt: number) => {
  toast.value.velocity = toast.value.velocity.lerp(VEC2(0, 0), dt * 0.025 * toast.value.invMass);

  const mag = toast.value.velocity.mag();

  if (mag <= 0.01) {
    toast.value.velocity = VEC2(0, 0);
  }
}

const integrate = (toast: Ref<Toast>, dt: number) => {
  toast.value.position = toast.value.position.add(
    toast.value.velocity.scale(dt * toast.value.invMass)
  );
}

const animate = (toast: Ref<Toast>, dt: number) => {
  const elapsed = getToastElapsed(toast.value);
  const duration = getToastDuration(toast.value);

  const n = clamp(elapsed / duration, 0, 1);

  if (n <= 0.01) {
    toastAddImpulse(toast, VEC2(0, 200).scale(toast.value.mass));
  } else if (n > 0.05 && (n < 0.95 || !toast.value.trash)) {
    if (toast.value.position.y < TOAST_SPACE_BOTTOM) {
      const depth = TOAST_SPACE_BOTTOM - toast.value.position.y;
      const norm = VEC2(0, 1);
      const sep = toast.value.velocity.scale(-1);
      const dot = -sep.dot(norm);
      const j = -(1.0 + 1.6) * dot;
      const imp = norm.scale(j);

      if (dot < 0) {
        toastAddImpulse(toast, imp);
      }
      toastAddPseudoImpulse(toast, norm.scale(dt*8*Math.max(0.0, depth-0.0001)));
    }
  }
}

export const updateToast = (state: UseToastsState,  toast: Ref<Toast>, dt: number) => {
  if (toast.value.height <= 0) return

  if (!canIntersect(toast.value)) {
    toast.value.animation.cancel()
    popToast(toast.value.uid, true)
    return
  }

  checkIntersections(toast, state, dt);

  tryToRest(toast, dt);

  animate(toast, dt);

  applyGravity(toast, dt);
  integrate(toast, dt);

  checkIntersections(toast, state, dt);
}
